import { useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IconButton, InputAdornment } from '@mui/material';
import Iconify from 'src/components/iconify';

import CasColorSwatch from '../CasColorSwatch/CasColorSwatch';
import CasRHFTextField, { CasRHFTextFieldProps } from './CasRHFTextField';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/*
  A #RRGGBB color field: type the value, or click the swatch at the start of the field to choose
  one with the browser's own color picker. Clearing the text stores null, so pair it with a
  nullable schema when the color is optional.
*/
export default function CasRHFColorField({ name, ...other }: CasRHFTextFieldProps) {
  const { control, setValue } = useFormContext();
  const value: string | null = useWatch({ control, name });
  const pickerRef = useRef<HTMLInputElement>(null);
  const isHex = !!value && HEX_COLOR.test(value);

  return (
    <CasRHFTextField
      name={name}
      emptyValueToNull
      placeholder="#RRGGBB"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconButton
              size="small"
              edge="start"
              aria-label="Choose a color"
              onClick={() => pickerRef.current?.click()}
            >
              {isHex ? <CasColorSwatch value={value} /> : <Iconify icon="ic:outline-palette" />}
            </IconButton>
            {/* Invisible, but laid out under the field so the browser opens its picker there. */}
            <input
              ref={pickerRef}
              type="color"
              tabIndex={-1}
              aria-hidden
              value={(isHex && value) || '#000000'}
              onChange={(event) =>
                setValue(name, event.target.value.toUpperCase(), {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              style={{ position: 'absolute', left: 0, bottom: 0, width: 0, height: 0, opacity: 0, border: 0, padding: 0 }}
            />
          </InputAdornment>
        ),
      }}
      {...other}
    />
  );
}
